import store from '../store'

/**
 * Locale-aware DISPLAY formatting helpers.
 *
 * The store's `country_code` picks the convention: we hand `en-<COUNTRY>` to
 * `Intl.DateTimeFormat` and let CLDR decide the field order, the date separator
 * and whether the clock runs 12- or 24-hour. No market is described here —
 * `en-PH` comes out 12-hour because the Philippines uses a 12-hour clock, not
 * because this file says so.
 *
 * Two house rules override CLDR, both documented at `normalizeTimeParts`: the
 * hour/minute separator is always a colon, and AM/PM is always "AM"/"PM".
 *
 * Every helper takes a moment (or a Date) and returns display text, so callers
 * never touch a format string.
 *
 * IMPORTANT: these helpers only affect what the user SEES. API payloads and
 * any value used for logic/comparison (ISO `YYYY-MM-DD`, military `HH:mm`,
 * timestamps, keys) must keep their fixed formats and must NOT use these.
 */

// Used only when a store has no `country_code` at all, which keeps the US
// behavior legacy stores already have.
const DEFAULT_LOCALE = 'en-US'

// CLDR has no `en-<COUNTRY>` entry for every country. Where it's missing, the tag
// degrades to bare `en`, which carries US conventions — the wrong default for
// most of the world. There is no data-derived answer to "which conventions
// should an English UI use in a country CLDR has no English data for", so this
// is a deliberate product default: day-first, 24-hour. It is not a claim about
// Britain, and it is not a per-market rule.
//
// Alternatives considered and rejected: bare `en` (US conventions), `en-001`
// (CLDR's "world English" — day-first but 12-hour, which would flip every
// fallback market off the 24-hour clock), and `en-150` (identical output to
// `en-GB`, but "Europe" is untrue for markets like BR or SA).
const FALLBACK_LOCALE = 'en-GB'

const localeByCountry = new Map()
const formatters = new Map()
const hourOptionsByLocale = new Map()

function resolveLocale(countryCode) {
  if (!countryCode) return DEFAULT_LOCALE

  // A region subtag is exactly two letters. `Intl` throws a RangeError on
  // anything else, so malformed codes take the fallback rather than bringing
  // down every date on the page.
  const country = String(countryCode).toUpperCase()
  if (!/^[A-Z]{2}$/.test(country)) return FALLBACK_LOCALE

  // `US` needs no special case: `en-US` comes out of this path like any other
  // country CLDR has data for.
  const tag = `en-${country}`
  // A resolved locale that kept its region means CLDR had data for this country.
  const resolved = new Intl.DateTimeFormat(tag).resolvedOptions().locale
  return /-[A-Z]{2}\b/.test(resolved) ? tag : FALLBACK_LOCALE
}

function localeTag() {
  const countryCode = store.state.storeConfig?.country_code ?? ''
  if (!localeByCountry.has(countryCode)) {
    localeByCountry.set(countryCode, resolveLocale(countryCode))
  }
  return localeByCountry.get(countryCode)
}

// A 24-hour clock is written zero-padded ("09:05"), a 12-hour one is not
// ("9:05 AM"). `Intl` won't infer that from the locale on its own, so we ask it
// which clock the locale uses and pad accordingly.
function hourOptions(locale) {
  if (!hourOptionsByLocale.has(locale)) {
    const is12Hour = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12
    hourOptionsByLocale.set(locale, {
      hour: is12Hour ? 'numeric' : '2-digit',
      minute: '2-digit',
    })
  }
  return hourOptionsByLocale.get(locale)
}

// Building an `Intl.DateTimeFormat` is expensive and the date picker formats
// hundreds of slots in a row, so each locale/shape pair is built once.
function formatter(shape, buildOptions) {
  const locale = localeTag()
  const key = `${locale}|${shape}`
  if (!formatters.has(key)) {
    formatters.set(key, new Intl.DateTimeFormat(locale, buildOptions(locale)))
  }
  return formatters.get(key)
}

// Unwraps a moment into a Date. Returns null for anything unusable so callers
// render an empty string instead of throwing a RangeError mid-render.
function toDate(value) {
  const date = typeof value?.toDate === 'function' ? value.toDate() : value
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null
}

// Two house rules applied on top of CLDR. Everything else — field order, 12- vs
// 24-hour, zero padding — stays CLDR's call.
//
//  1. Hour/minute separator is always a colon. 101 of the 104 `en-*` locales
//     already use one; DK, FI and ID use a period, and "14.05" reads like a
//     decimal or a date, worse still inside "Open from 09.00 to 17.00".
//  2. AM/PM is always uppercase and undotted. CLDR spells it "am" in 45 locales,
//     "AM" in 10 and "a.m." in Canada; the app has always shown "AM"/"PM", so we
//     keep one spelling rather than letting it vary per market.
function normalizeTimeParts(parts) {
  return parts
    .map((part, i) => {
      if (part.type === 'dayPeriod') return part.value.replace(/\./g, '').toUpperCase()
      const separatesHourFromMinute =
        part.type === 'literal' &&
        part.value === '.' &&
        parts[i - 1]?.type === 'hour' &&
        parts[i + 1]?.type === 'minute'
      return separatesHourFromMinute ? ':' : part.value
    })
    .join('')
}

function display(value, shape, buildOptions) {
  const date = toDate(value)
  if (!date) return ''
  return normalizeTimeParts(formatter(shape, buildOptions).formatToParts(date))
}

// Time of day, e.g. "2:05 PM" (US) vs "14:05" (non-US).
export function displayTime(value) {
  return display(value, 'time', hourOptions)
}

// Short numeric date without a year, e.g. "07/16" (US) vs "16/07" (non-US).
export function displayShortDate(value) {
  return display(value, 'shortDate', () => ({ day: '2-digit', month: '2-digit' }))
}

// Full date with a spelled-out month, e.g. "Jul 16, 2026" (US) vs
// "16 Jul 2026" (non-US).
export function displayFullDate(value) {
  return display(value, 'fullDate', () => ({ day: 'numeric', month: 'short', year: 'numeric' }))
}

// Full date + time, honoring both the field order and the clock convention.
export function displayFullDateTime(value) {
  return display(value, 'fullDateTime', (locale) => ({
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...hourOptions(locale),
  }))
}
